import React, {useEffect, useMemo, useState} from "react";
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell,
        TableHead, TableRow, Typography} from "@mui/material";
import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import {Grey6, Grey17, Red3} from "../../theme/styles";

type AuditEvent = {
    USID: string;
    Action: "UPLOAD" | "PREVIEW" | "DOWNLOAD" | string;
    At?: string | null;
    ByName?: string | null;
    ByEmail?: string | null;
    FileName?: string | null;
    Detail?: string | null;
    RecordLabel?: string | null;
};

const ACTION_LABEL: Record<string, string> = {UPLOAD: "Uploaded", PREVIEW: "Previewed", DOWNLOAD: "Downloaded"};

/**
 * One file's audit trail: who uploaded it, and who has previewed or downloaded it since, newest first.
 *
 * THE FILE'S TRAIL, NOT THE RECORD'S. Attaching and removing are lines on the record's own history; this
 * is the file, across every revision that carried it -- filtered by the server to the rows reached through
 * a record this persona can see, and the count of the rest is said rather than hidden, because a trail
 * that silently drops rows reads as a complete one.
 *
 * A DOWNLOAD HERE IS A URL HANDED OUT. The bytes come from S3, never through the platform, so what is
 * known is that this person was given the file, not that their browser finished saving it.
 */
export default function AttachmentAuditTrailDialog({attachmentUSID, name, onClose}: {attachmentUSID: string; name: string; onClose: () => void}) {
    const sessionContext = useSessionContext();
    const Axios = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);
    const [events, setEvents] = useState<AuditEvent[] | null>(null);
    const [withheld, setWithheld] = useState(0);
    const [truncated, setTruncated] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        Axios({method: "GET", url: "t1/v1/attachment_audit_events", params: {AttachmentUSID: attachmentUSID}})
            .then((response: any) => {
                setEvents(response?.data?.Events ?? []);
                setWithheld(response?.data?.Withheld ?? 0);
                setTruncated(!!response?.data?.Truncated);
            })
            .catch((problem: any) => setError(problem?.response?.data?.Cause ?? "This file's audit trail could not be read."));
    }, [Axios, attachmentUSID]);

    const when = (at?: string | null) => (at ? new Date(at).toLocaleString() : "—");
    const head = {fontSize: 12, fontWeight: 700, color: Grey17};
    const cell = {fontSize: 13, color: Grey17, verticalAlign: "top" as const};

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth data-testid="attachment-audit-trail">
            <DialogTitle style={{fontSize: 16, fontWeight: 600, color: Grey17, paddingBottom: 4}}>Audit trail — {name}</DialogTitle>
            <DialogContent>
                <Typography style={{fontSize: 12, color: Grey6, marginBottom: 10, lineHeight: 1.5}}>
                    Every upload, preview and download of this file, newest first. A download is the moment the file was
                    handed to the person; it comes straight from storage, so whether their browser finished saving it is not known.
                </Typography>
                {error ? <Typography style={{fontSize: 13, color: Red3}}>{error}</Typography>
                    : events === null ? <CircularProgress size={20}/>
                    : events.length === 0 ? <Typography style={{fontSize: 13, color: Grey6}}>Nothing recorded for this file yet.</Typography>
                    : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell style={head}>When</TableCell>
                                    <TableCell style={head}>Action</TableCell>
                                    <TableCell style={head}>By</TableCell>
                                    <TableCell style={head}>Record</TableCell>
                                    <TableCell style={head}>Detail</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map(one => (
                                    <TableRow key={one.USID} data-testid="attachment-audit-row">
                                        <TableCell style={{...cell, whiteSpace: "nowrap"}}>{when(one.At)}</TableCell>
                                        <TableCell style={cell}>{ACTION_LABEL[one.Action] ?? one.Action}</TableCell>
                                        <TableCell style={cell}>
                                            {one.ByName || "—"}
                                            {one.ByEmail ? <div style={{fontSize: 11.5, color: Grey6}}>{one.ByEmail}</div> : null}
                                        </TableCell>
                                        <TableCell style={{...cell, whiteSpace: "nowrap"}}>{one.RecordLabel || "—"}</TableCell>
                                        <TableCell style={{...cell, color: Grey6}}>
                                            {[one.FileName && one.FileName !== name ? `as ${one.FileName}` : null, one.Detail].filter(Boolean).join("; ") || "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                {withheld > 0 ? (
                    <Typography style={{fontSize: 12, color: Grey6, marginTop: 10}}>
                        {withheld} more {withheld === 1 ? "entry was" : "entries were"} made through {withheld === 1 ? "a record" : "records"} you cannot see, and {withheld === 1 ? "is" : "are"} not shown.
                    </Typography>
                ) : null}
                {truncated ? (
                    <Typography style={{fontSize: 12, color: Grey6, marginTop: 6}}>Only the most recent entries are shown.</Typography>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
